"use client";

import { useMemo, useRef, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Panel,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  HINT,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  buildSpeechOutline,
  MONROE_BODY_STEPS,
  type SpeechOutlineInput,
  type SpeechPattern,
  type SpeechPoint,
  type SpeechType,
} from "@/lib/tool-kit/essay-templates/speech-outline";

/** English strings. A translated content file overrides these through `ui`. */
export const SPEECH_OUTLINE_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  typeLabel: "Speech type",
  types: { informative: "Informative", persuasive: "Persuasive" },
  patternLabel: "Organization",
  patterns: {
    topical: "Main points",
    monroe: "Monroe's Motivated Sequence",
  },
  patternHint:
    "Monroe's sequence turns the body into Need, Satisfaction, and Visualization, and ends on a call to Action.",
  basicsHeading: "Topic, purpose, and central idea",
  topicLabel: "Topic",
  topicPlaceholder: "e.g. The honeybee waggle dance",
  purposeLabel: "Specific purpose",
  purposePlaceholders: {
    informative: "To inform my audience about …",
    persuasive: "To persuade my audience to …",
  },
  centralLabel: "Central idea (thesis)",
  centralPlaceholder: "One full sentence that sums up the whole speech.",
  introHeading: "Introduction",
  attentionLabel: "Attention-getter",
  attentionPlaceholder: "A story, a question, a quotation, or a striking fact.",
  credibilityLabel: "Credibility statement",
  credibilityPlaceholder: "Why you can speak on this topic.",
  previewLabel: "Preview of your main points",
  previewPlaceholder: "Today I'll explain …, …, and ….",
  bodyHeading: "Body",
  pointLabel: "Main point {n}",
  monroeLabels: {
    need: "Need — the problem, and why it matters to this audience",
    satisfaction: "Satisfaction — your solution",
    visualization: "Visualization — the future with (or without) your solution",
  },
  mainLabel: "The point, as one full sentence",
  subsLabel: "Sub-points — one per line",
  subsHint:
    "Start a line with “-” to make it a supporting detail of the line above.",
  subsPlaceholder:
    "e.g. A first supporting point\n- An example or statistic for it",
  transitionLabel: "Transition to the next point",
  transitionPlaceholder: "e.g. Now that you know …, let's look at …",
  addPoint: "Add a main point",
  removePoint: "Remove",
  removePointLabel: "Remove main point {n}",
  conclusionHeading: "Conclusion",
  summaryLabel: "Summary of your main points",
  summaryLabelOptional: "Summary of your main points (optional)",
  summaryPlaceholder: "Signal the end and restate your main points.",
  actionLabel: "Action — what exactly should the audience do?",
  actionPlaceholder: "One specific, doable step, with how and when.",
  closeLabel: "Memorable close",
  closePlaceholder: "An image, a quotation, or a callback to your opening.",
  fileName: "speech-outline",
  document: {
    heading: "Your speech outline",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every part of the outline has your content.",
  },
  defaultTitle: "Speech Outline",
  subtitles: {
    informative: "Informative speech · full-sentence outline",
    persuasive: "Persuasive speech · full-sentence outline",
    monroe: "Persuasive speech · Monroe's Motivated Sequence",
  },
  fields: {
    topic: "Topic",
    purpose: "Specific purpose",
    central: "Central idea",
  },
  headings: {
    introduction: "Introduction",
    introductionMonroe: "Introduction — Attention",
    body: "Body",
    conclusion: "Conclusion",
    conclusionMonroe: "Conclusion — Action",
    references: "References",
  },
  items: {
    attention: "Attention-getter",
    credibility: "Credibility",
    preview: "Preview",
    summary: "Summary",
    action: "Action",
    close: "Memorable close",
    transition: "Transition",
  },
  monroeSteps: {
    need: "Need",
    satisfaction: "Satisfaction",
    visualization: "Visualization",
  },
  prompts: {
    topic: "Name your topic in a few words.",
    purposeInformative:
      "Write one infinitive phrase that names your goal: “To inform my audience about …”.",
    purposePersuasive:
      "Write one infinitive phrase that names the belief or action you want: “To persuade my audience to …”.",
    central:
      "Sum up the whole speech in one full sentence; every main point should follow from it.",
    attention:
      "Attention-getter — open with a story, a question, a quotation, or a striking fact that leads into your topic.",
    credibility:
      "Credibility — tell the audience why you can speak on this topic: experience, study, or research.",
    preview: "Preview — name your main points in one sentence.",
    previewWithPoints:
      "Preview — name your main points in one sentence: {points}.",
    mainPoint:
      "Main point {n} — state it as one full sentence that supports your central idea.",
    support:
      "Support the point with facts, examples, statistics, or testimony — one full sentence each, naming the source.",
    transition:
      "Transition — link point {from} to point {to}: sum up the first, then introduce the second.",
    summary: "Summary — signal the end and restate your main points.",
    summaryWithPoints:
      "Summary — signal the end and restate your main points: {points}.",
    action: "Action — tell the audience exactly what to do, how, and by when.",
    close:
      "Memorable close — end with an image, a quotation, or a callback to your opening.",
    references: "Add your sources here.",
  },
  monroePrompts: {
    need: "Need — show the problem: what is wrong, why it matters to this audience, and the evidence for it.",
    satisfaction:
      "Satisfaction — present your solution and show how it meets the need.",
    visualization:
      "Visualization — help the audience picture the future with your solution, or without it.",
  },
  referencesNote:
    "List every source you quote or cite aloud, in the style your instructor requires (often APA or MLA). In the speech itself, name each source as you use it: “According to …”.",
  exampleInformative: {
    topic: "The honeybee waggle dance",
    purpose:
      "To inform my audience how honeybees use the waggle dance to tell their nestmates where to find food.",
    central:
      "Honeybees perform a waggle dance that encodes both the direction and the distance of a food source — a code first worked out by Karl von Frisch.",
    attention:
      "Imagine giving a friend directions to a café a mile away — in total darkness, without saying a word. Honeybees do this every day.",
    credibility:
      "I keep two hives with the campus beekeeping club and have watched this dance through our observation frame.",
    preview:
      "I'll explain what the waggle dance is, how it points to the food, and how it shows the distance.",
    summary:
      "Today you've learned what the waggle dance is, how its angle gives direction, and how its length gives distance.",
    action: "",
    close:
      "So the next time a bee lands near you, remember: she may be about to give her sisters directions.",
  },
  exampleInformativePoints: [
    "The waggle dance is a figure-eight movement a forager performs on the comb after finding a good source of food.",
    "The angle of the waggle run tells the other bees which direction to fly.",
    "The length of the waggle run tells the other bees how far away the food is.",
  ],
  exampleInformativeSubs: [
    "The bee runs forward in a straight line while waggling her abdomen, then circles back to repeat the run.\nOther bees follow the dancer and pick up the scent of the flowers she visited.\nKarl von Frisch decoded the dance and shared the 1973 Nobel Prize in Physiology or Medicine.",
    "A run straight up the comb means “fly toward the sun.”\nA run at an angle to the left or right of vertical means fly at that same angle to the left or right of the sun.\n- Bees change the angle as the sun moves across the sky.",
    "The longer the waggle run lasts, the farther away the food is.\nBees measure distance by how much of the landscape streams past their eyes in flight.",
  ],
  exampleInformativeTransitions: [
    "Now that you know what the dance looks like, let's see how it gives directions.",
    "Direction is only half the message — the bees also need to know how far to go.",
    "",
  ],
  examplePersuasive: {
    topic: "Use a password manager",
    purpose:
      "To persuade my audience to start using a password manager this week.",
    central:
      "Reusing passwords puts every account you own at risk, and a password manager fixes the problem in about ten minutes.",
    attention:
      "Raise your hand if you've used the same password on more than one site. Keep it up if that password is also on your email account.",
    credibility:
      "I'm a computer science major, and last year a breached shopping site exposed my own reused password.",
    preview:
      "I'll show you why reused passwords are dangerous, how a password manager solves that, and what your accounts look like once you switch.",
    summary: "",
    action:
      "Before Friday, install a password manager, save your email password in it, and change that password to one the manager generates.",
    close:
      "One breach is bad luck. Every account falling with it is a choice — choose differently this week.",
  },
  examplePersuasivePoints: [
    "Reused passwords turn one website's data breach into a break-in at all of your accounts.",
    "A password manager creates and remembers a strong, unique password for every account.",
    "With a manager in place, the next breach costs you one password instead of every account.",
  ],
  examplePersuasiveSubs: [
    "When a site is breached, attackers automatically try the stolen email-and-password pairs on other sites — an attack called credential stuffing.\nMost of us reuse passwords because nobody can remember dozens of strong, unique ones.",
    "You remember one strong master password, and the manager fills in the rest.\nMany are free, and most phones and browsers now include one.\nTurn on two-factor authentication for the manager itself.",
    "You change a single password and move on.\nWithout one, you spend a weekend resetting accounts and hoping you found them all.",
  ],
  examplePersuasiveTransitions: [
    "The fix isn't a better memory — it's a better tool.",
    "Picture what changes once it's set up.",
    "",
  ],
} as const;

interface PointState extends SpeechPoint {
  id: number;
}

type TextKey = Exclude<keyof SpeechOutlineInput, "type" | "pattern" | "points">;

const MIN_POINTS = 2;
const MAX_POINTS = 5;
const BLANK: Omit<SpeechOutlineInput, "type" | "pattern" | "points"> = {
  topic: "",
  purpose: "",
  central: "",
  attention: "",
  credibility: "",
  preview: "",
  action: "",
  summary: "",
  close: "",
};

function blankPoints(): PointState[] {
  return [1, 2, 3].map((id) => ({ id, main: "", subs: "", transition: "" }));
}

export default function SpeechOutlineGeneratorWidget() {
  const strings = useToolUi(SPEECH_OUTLINE_GENERATOR_STRINGS);
  const nextId = useRef(4);
  const [type, setType] = useState<SpeechType>("informative");
  const [pattern, setPattern] = useState<SpeechPattern>("topical");
  const [fields, setFields] = useState(BLANK);
  const [points, setPoints] = useState<PointState[]>(blankPoints);
  const monroe = type === "persuasive" && pattern === "monroe";
  const shown = useMemo(
    () => (monroe ? points.slice(0, MONROE_BODY_STEPS.length) : points),
    [monroe, points]
  );
  const doc = useMemo(
    () =>
      buildSpeechOutline({ ...fields, type, pattern, points: shown }, strings),
    [fields, type, pattern, shown, strings]
  );
  const update = (key: TextKey) => (value: string) =>
    setFields((previous) => ({ ...previous, [key]: value }));
  const area = (key: TextKey, label: string, placeholder: string) => (
    <TextAreaField
      id={`so-${key}`}
      label={label}
      onChange={update(key)}
      placeholder={placeholder}
      prose
      rows={2}
      value={fields[key]}
    />
  );
  const updatePoint = (id: number, patch: Partial<SpeechPoint>) =>
    setPoints((previous) =>
      previous.map((point) =>
        point.id === id ? { ...point, ...patch } : point
      )
    );

  function fresh(count: number): PointState[] {
    return Array.from({ length: count }, () => {
      nextId.current += 1;
      return { id: nextId.current, main: "", subs: "", transition: "" };
    });
  }

  function choosePattern(value: SpeechPattern) {
    setPattern(value);
    if (value === "monroe" && points.length < MONROE_BODY_STEPS.length) {
      setPoints([
        ...points,
        ...fresh(MONROE_BODY_STEPS.length - points.length),
      ]);
    }
  }

  function loadExample() {
    const persuasive = type === "persuasive";
    const example = persuasive
      ? strings.examplePersuasive
      : strings.exampleInformative;
    const mains = persuasive
      ? strings.examplePersuasivePoints
      : strings.exampleInformativePoints;
    const subs = persuasive
      ? strings.examplePersuasiveSubs
      : strings.exampleInformativeSubs;
    const transitions = persuasive
      ? strings.examplePersuasiveTransitions
      : strings.exampleInformativeTransitions;
    setFields({ ...BLANK, ...example });
    setPoints(
      fresh(mains.length).map((point, index) => ({
        ...point,
        main: mains[index] ?? "",
        subs: subs[index] ?? "",
        transition: transitions[index] ?? "",
      }))
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={loadExample}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setFields(BLANK);
            setPoints(fresh(3));
          }}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <PillGroup
          label={strings.typeLabel}
          name="so-type"
          onChange={setType}
          options={(["informative", "persuasive"] as const).map((value) => ({
            value,
            label: strings.types[value],
          }))}
          value={type}
        />
        {type === "persuasive" ? (
          <div className="space-y-2">
            <PillGroup
              label={strings.patternLabel}
              name="so-pattern"
              onChange={choosePattern}
              options={(["topical", "monroe"] as const).map((value) => ({
                value,
                label: strings.patterns[value],
              }))}
              value={pattern}
            />
            <p className={HINT}>{strings.patternHint}</p>
          </div>
        ) : null}
      </div>

      <Panel title={strings.basicsHeading}>
        <TextField
          id="so-topic"
          label={strings.topicLabel}
          onChange={update("topic")}
          placeholder={strings.topicPlaceholder}
          value={fields.topic}
        />
        <TextField
          id="so-purpose"
          label={strings.purposeLabel}
          onChange={update("purpose")}
          placeholder={strings.purposePlaceholders[type]}
          value={fields.purpose}
        />
        {area("central", strings.centralLabel, strings.centralPlaceholder)}
      </Panel>

      <Panel title={strings.introHeading}>
        {area(
          "attention",
          strings.attentionLabel,
          strings.attentionPlaceholder
        )}
        {area(
          "credibility",
          strings.credibilityLabel,
          strings.credibilityPlaceholder
        )}
        {area("preview", strings.previewLabel, strings.previewPlaceholder)}
      </Panel>

      <section className="space-y-4">
        <h3 className={WIDGET_H3}>{strings.bodyHeading}</h3>
        {shown.map((point, index) => {
          const step = monroe ? MONROE_BODY_STEPS[index] : undefined;
          const heading = step
            ? strings.monroeLabels[step]
            : t(strings.pointLabel, { n: index + 1 });
          return (
            <fieldset className={`${CARD} space-y-4`} key={point.id}>
              <legend className="px-1 font-medium text-foreground text-sm">
                {heading}
              </legend>
              <TextAreaField
                id={`so-main-${point.id}`}
                label={strings.mainLabel}
                onChange={(main) => updatePoint(point.id, { main })}
                prose
                rows={2}
                value={point.main}
              />
              <TextAreaField
                hint={strings.subsHint}
                id={`so-subs-${point.id}`}
                label={strings.subsLabel}
                onChange={(subs) => updatePoint(point.id, { subs })}
                placeholder={strings.subsPlaceholder}
                prose
                rows={3}
                value={point.subs}
              />
              {index < shown.length - 1 ? (
                <TextField
                  id={`so-transition-${point.id}`}
                  label={strings.transitionLabel}
                  onChange={(transition) =>
                    updatePoint(point.id, { transition })
                  }
                  placeholder={strings.transitionPlaceholder}
                  value={point.transition}
                />
              ) : null}
              {!monroe && points.length > MIN_POINTS ? (
                <button
                  aria-label={t(strings.removePointLabel, { n: index + 1 })}
                  className={BUTTON_SMALL}
                  onClick={() =>
                    setPoints((previous) =>
                      previous.filter((other) => other.id !== point.id)
                    )
                  }
                  type="button"
                >
                  {strings.removePoint}
                </button>
              ) : null}
            </fieldset>
          );
        })}
        {!monroe && points.length < MAX_POINTS ? (
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setPoints((previous) => [...previous, ...fresh(1)])}
            type="button"
          >
            {strings.addPoint}
          </button>
        ) : null}
      </section>

      <Panel title={strings.conclusionHeading}>
        {area(
          "summary",
          monroe ? strings.summaryLabelOptional : strings.summaryLabel,
          strings.summaryPlaceholder
        )}
        {monroe
          ? area("action", strings.actionLabel, strings.actionPlaceholder)
          : null}
        {area("close", strings.closeLabel, strings.closePlaceholder)}
      </Panel>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="so"
        labels={strings.document}
      />
    </div>
  );
}
